import React from 'react';
import style from './style.module.scss';
import {Typography} from 'components/Typography';
import {formatPrice} from 'core/helpers';
import cs from 'classnames';
import {SpecialPriceBadge} from './SpecialPriceLabel';
import {UnitPriceBadge} from './UnitPriceBadge';
import {IProductSnippet} from 'shared/types/productSnippet.types';
import {CashbackLabel} from 'components/ProductSnippetNew/components/Labels/CashbackLabel';
import {RUB_SYMBOL} from 'core/constants';
import {OnlineDiscountBadge} from 'components/ProductSnippetNew/components/ProductSnippetContent/Price/OnlineDiscountBadge';

type TPriceProps = IProductSnippet['price'];

const CURRENCY_MAP = {
    ['RUB']: RUB_SYMBOL,
};

interface IGetPriceCurrency {
    currency?: 'RUB' | null;
    value: number | null;
}

const getPriceWithCurrency = ({currency, value}: IGetPriceCurrency) => {
    const currencyValue = CURRENCY_MAP[currency as keyof typeof CURRENCY_MAP] || CURRENCY_MAP.RUB;

    return `${formatPrice(value || 0)} ${currencyValue}`;
};

export const Price = (props: TPriceProps) => {
    const {unit, currency, cashback, percent, old, current} = props;

    return (
        <div className={style.main}>
            <Price.Badges badges={props.badges} unit={unit} currency={currency} />
            <Price.Values cashback={cashback} currency={currency} current={current} percent={percent} old={old} />
        </div>
    );
};

Price.Base = (props: Pick<TPriceProps, 'currency' | 'current' | 'cashback'>) => {
    const {current, currency, cashback} = props;

    const currentPrice = getPriceWithCurrency({value: current, currency});

    return (
        <div className={cs(style.main, style.main__price)}>
            <div className={style.price}>
                <Typography
                    variants={{DT: 'price-24-b', TB: 'price-20-b', MB: 'price-20-b'}}
                    className={cs(style.price__value)}
                >
                    {currentPrice}
                </Typography>
                {cashback && <CashbackLabel value={cashback} />}
            </div>
        </div>
    );
};

Price.Values = (props: Pick<TPriceProps, 'old' | 'current' | 'currency' | 'percent' | 'cashback'>) => {
    const {current, old, currency, percent, cashback} = props;

    const oldPrice = getPriceWithCurrency({value: old, currency});
    const currentPrice = getPriceWithCurrency({value: current, currency});

    return (
        <div className={cs(style.main__price)} data-skeleton-item={true}>
            <div className={style.discount}>
                {Boolean(cashback) && <CashbackLabel value={cashback} />}
                {(Boolean(old) || Boolean(percent)) && (
                    <div className={style.discount__old}>
                        {Boolean(old) && (
                            <Typography className={style.discount__value} variant={'label-14-r'}>
                                {oldPrice}
                            </Typography>
                        )}
                        {Boolean(percent) && (
                            <Typography className={style.discount__percent} variant={'label-12-r'}>
                                -{percent}%
                            </Typography>
                        )}
                    </div>
                )}
            </div>
            <div className={style.price}>
                <Typography variant={'title-20-m'} className={style.price__value}>
                    {currentPrice}
                </Typography>
            </div>
        </div>
    );
};

// eslint-disable-next-line complexity
Price.Badges = (props: Pick<TPriceProps, 'badges' | 'unit' | 'currency'>) => {
    const {
        unit,
        currency,
        badges: {hasUnitPrice, isSpecialPrice, isOnlineDiscount, isOutOfStock},
    } = props;

    const unitPrice = getPriceWithCurrency({value: unit, currency});

    if (!hasUnitPrice && !isOnlineDiscount && !isSpecialPrice && !isOutOfStock) {
        return null;
    }

    return (
        <div className={style.labels} data-skeleton-item={true}>
            {isSpecialPrice && <SpecialPriceBadge className={style.badge} />}
            {hasUnitPrice && <UnitPriceBadge className={style.badge} unitPrice={unitPrice} />}
            {isOnlineDiscount && <OnlineDiscountBadge />}
            {/* {isOnlineOnly && <OnlineOnlyBadge className={style.badge} />}*/}
            {/* {isOutOfStock && <OutOfStockBadge className={style.badge} />}*/}
        </div>
    );
};
